import React, { useEffect, useRef, useState } from 'react'
import { FaEdit, FaUndo } from 'react-icons/fa'
import ModalComponent from '../atoms/Modal/ModalComponent/ModalComponent'
import ThreeDots from '../../assets/icons/more-vertical.svg'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { BeatLoader } from 'react-spinners'
import { useNavigate } from 'react-router-dom'
import { REFUND_ORDER } from '../../apollo/mutations/refundOrder'
import { useMutation } from '@apollo/client'
import { createToast } from '../../utils/Toast'
import { formatString, updateStatusOptions } from '../../utils'
import FormInput from '../atoms/Input/Form-Input'
import { UPDATE_STATUS } from '../../apollo/mutations/updateTransactionStatus'
import ActionButton from '../Common/ActionButton'

const Toast = createToast()

dayjs.extend(customParseFormat)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

function OrderTable({ orders, paginationItems, loading }) {
  const [openDropdown, setOpenDropdown] = useState(null)
  const [openModal, setOpenModal] = useState(false)
  const [activeIndex, setActiveIndex] = useState(null)
  const [openEditModal, setOpenEditModal] = useState(false)
  const [cancelModal, setCancelModal] = useState(false)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null })
  const dropdownRef = useRef(null)
  const navigate = useNavigate()
  const [refundOrder] = useMutation(REFUND_ORDER)
  const [processingRefundId, setProcessingRefundId] = useState(null)
  const [selectedOrder, setSelectedOrder] = useState(null)
  console.log({ selectedOrder })
  const [orderStatus, setOrderStatus] = useState('')
  const [selectedOrderInitialStatus, setSelectedOrderInitialStatus] = useState('')
  const [updateStatus, { loading: updateStatusLoading }] =
    useMutation(UPDATE_STATUS)

  const handleEditStatus = order => {
    setSelectedOrder(order)
    setOrderStatus(order.orderStatus !== 'N/A' ? order.orderStatus : '')
    setSelectedOrderInitialStatus(order.orderStatus !== 'N/A' ? order.orderStatus : '')
    setOpenModal(true)
    setOpenEditModal(true)
  }

  const transactions = (paginationItems || orders)?.map(item => ({
    id: item?.id,
    order: item.orderId,
    date: item.shopify?.orderDate,
    formattedDate: dayjs(item.createdAt).format(
      'MMM Do YYYY [at] h:mm A',
    ),
    total: `${item.totalPrice.toLocaleString()}`,
    status: item.status,
    paymentStatus: item.paymentStatus,
    fulfillmentStatus: item.shopify?.fulfillmentStatus || 'N/A',
    items: item.shopifyItems?.length,
    deliveryStatus: item?.shopifyDeliveryStatus || 'N/A',
    brandName: item.brandName,
    productId: item.productId,
    orderStatus: item?.orderStatus?.length
      ? [...item.orderStatus]
        .sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp))
        .map(status => status?.status)[0]
      : 'N/A',
    isRefundRequestSent: item?.isRefundRequestSent || false,
  }))

  const sortedTransactions = React.useMemo(() => {
    if (sortConfig.key) {
      return [...transactions].sort((a, b) => {
        const getValue = item => {
          switch (sortConfig.key) {
            case 'date':
              return item.date ? new Date(item.date).getTime() : 0
            case 'total':
              return typeof item.total === 'string'
                ? parseFloat(item.total.replace(/[$,]/g, '')) || 0
                : item.total || 0
            case 'items':
              return item.items || 0
            case 'order':
            case 'status':
            case 'paymentStatus':
            case 'fulfillmentStatus':
            case 'orderStatus':
            case 'deliveryStatus':
              return item[sortConfig.key]?.toString().toLowerCase() || ''
            default:
              return ''
          }
        }

        const aValue = getValue(a)
        const bValue = getValue(b)

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue)
        } else {
          return sortConfig.direction === 'ascending'
            ? aValue - bValue
            : bValue - aValue
        }
      })
    }
    return transactions
  }, [transactions, sortConfig])

  const getStatusColor = status => {
    switch (status) {
      case 'PENDING':
        return 'text-yellow-500'
      case 'CONFIRMED':
        return 'text-green-500'
      case 'IN_TRANSIT':
        return 'text-blue-500'
      case 'DELIVERED':
        return 'text-gray-500'
      case 'CANCELLED':
        return 'text-red-500'
      default:
        return 'text-gray-400'
    }
  }

  const handleSort = key => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key && prevConfig.direction === 'ascending') {
        return { key, direction: 'descending' }
      }
      return { key, direction: 'ascending' }
    })
  }

  const handleViewOrder = item => {
    console.log({ item })
    const cleanId = item?.order.replace('#', '')
    navigate(`/order/${cleanId}`, {
      state: { item },
    })
  }

  const handleUpdateStatus = async () => {
    try {
      await updateStatus({
        variables: {
          transactionId: selectedOrder?.id,
          status: orderStatus,
        },
        refetchQueries: ['listTransactions'],
      })
      setOpenModal(false)
      Toast.fire({
        icon: 'success',
        title: 'Order Status Updated Successfully!',
      })
    } catch (error) {
      Toast.fire({ icon: 'error', title: error })
    }
  }

  const handleClickOutside = event => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setOpenDropdown(null)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleProcessRefund = async orderId => {
    setProcessingRefundId(orderId)
    try {
      await refundOrder({
        variables: {
          id: orderId,
        },
        refetchQueries: ['listTransactions'],
      })
      Toast.fire({ icon: 'success', title: 'Refund Requested Successfully!' })
    } catch (error) {
      Toast.fire({ icon: 'error', title: error })
    } finally {
      setProcessingRefundId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <BeatLoader color={'#2b364b'} />
      </div>
    )
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="flex justify-center items-center mt-8">
        <p className="admin-dashboard text-3xl text-gray-500">
          No orders available. Please check back later.
        </p>
      </div>
    )
  }

  return (
    <div className="w-full overflow-x-auto">
      <table
        className="min-w-full overflow-hidden"
        style={{
          border: '1px solid #999',
          borderRadius: '10px',
          borderSpacing: '0',
        }}
      >
        <thead className="thead">
          <tr className="table-head">
            <th
              scope="col"
              align="center"
              className="w-auto group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('order')}
            >
              Order
              <span className="text-sm ml-1">
                {sortConfig.key === 'order' ? (
                  sortConfig.direction === 'ascending' ? (
                    '↑'
                  ) : (
                    '↓'
                  )
                ) : (
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    ↑
                  </span>
                )}
              </span>
            </th>

            <th
              scope="col"
              align="start"
              className="px-3 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('date')}
            >
              Date
              <span className="text-sm ml-1">
                {sortConfig.key === 'date' ? (
                  sortConfig.direction === 'ascending' ? (
                    '↑'
                  ) : (
                    '↓'
                  )
                ) : (
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    ↑
                  </span>
                )}
              </span>
            </th>

            <th
              scope="col"
              align="start"
              className="w-auto px-3 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('total')}
            >
              Total
              <span className="text-sm ml-1">
                {sortConfig.key === 'total' ? (
                  sortConfig.direction === 'ascending' ? (
                    '↑'
                  ) : (
                    '↓'
                  )
                ) : (
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    ↑
                  </span>
                )}
              </span>
            </th>
            {/* <th
              scope="col"
              align="start"
              className="px-3 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('status')}
            >
              Status
              <span className="text-sm ml-1">
                {sortConfig.key === 'status' ? (
                  sortConfig.direction === 'ascending' ? (
                    '↑'
                  ) : (
                    '↓'
                  )
                ) : (
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    ↑
                  </span>
                )}
              </span>
            </th> */}
            <th
              scope="col"
              align="start"
              className="px-3 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('paymentStatus')}
            >
              Payment Status
              <span className="text-sm ml-1">
                {sortConfig.key === 'paymentStatus' ? (
                  sortConfig.direction === 'ascending' ? (
                    '↑'
                  ) : (
                    '↓'
                  )
                ) : (
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    ↑
                  </span>
                )}
              </span>
            </th>
            <th
              scope="col"
              align="start"
              className="px-3 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('fulfillmentStatus')}
            >
              Fulfillment Status
              <span className="text-sm ml-1">
                {sortConfig.key === 'fulfillmentStatus' ? (
                  sortConfig.direction === 'ascending' ? (
                    '↑'
                  ) : (
                    '↓'
                  )
                ) : (
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    ↑
                  </span>
                )}
              </span>
            </th>
            <th
              scope="col"
              align="start"
              className="px-3 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('orderStatus')}
            >
              Order Status
              <span className="text-sm ml-1">
                {sortConfig.key === 'orderStatus' ? (
                  sortConfig.direction === 'ascending' ? (
                    '↑'
                  ) : (
                    '↓'
                  )
                ) : (
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    ↑
                  </span>
                )}
              </span>
            </th>
            {/* <th
              scope="col"
              align="start"
              className="px-3 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('items')}
            >
              Items
              <span className="text-sm ml-1">
                {sortConfig.key === 'items' ? (
                  sortConfig.direction === 'ascending' ? (
                    '↑'
                  ) : (
                    '↓'
                  )
                ) : (
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    ↑
                  </span>
                )}
              </span>
            </th> */}
            {/* <th
              scope="col"
              align="start"
              className="px-3 group"
              style={{ borderBottom: '1px solid #999', cursor: 'pointer' }}
              onClick={() => handleSort('deliveryStatus')}
            >
              Delivery Status
              <span className="text-sm ml-1">
                {sortConfig.key === 'deliveryStatus' ? (
                  sortConfig.direction === 'ascending' ? (
                    '↑'
                  ) : (
                    '↓'
                  )
                ) : (
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    ↑
                  </span>
                )}
              </span>
            </th> */}
            <th
              scope="col"
              align="center"
              className="px-3 group"
              style={{ borderBottom: '1px solid #999' }}
            >
              Actions
            </th>

            {/* <th
                scope="col"
                align="start"
                className="px-3"
                style={{ borderBottom: '1px solid #999' }}
              ></th> */}
          </tr>
        </thead>
        <tbody>
          {sortedTransactions?.map((item, index) => (
            <tr
              key={index}
              className="cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={() => handleViewOrder(item)}
            >
              <td className="px-6 py-4 whitespace-nowrap table-order" style={{ borderBottom: '1px solid #999' }}>
                {item.order}
              </td>
              <td align="start" className="table-date px-3  whitespace-nowrap" style={{ borderBottom: '1px solid #999' }}>
                {item.formattedDate}
              </td>
              <td align="start" className="px-3 whitespace-nowrap table-order" style={{ borderBottom: '1px solid #999' }}>
                {item.total}
              </td>
              <td align="start" className={`table-status px-3 whitespace-nowrap  text-[16px]  ${item?.paymentStatus === 'PAID' && 'text-green-500'} ${['PENDING', 'REFUNDED']?.includes(item?.paymentStatus) && 'text-yellow-500'} ${['FAILED', 'CANCELLED']?.includes(item?.paymentStatus) && 'text-red-500'}`} style={{ borderBottom: '1px solid #999' }}>
                {formatString(item.paymentStatus)}
              </td>
              <td align="start" className="table-buyer px-3  whitespace-nowrap text-[16px]" style={{ borderBottom: '1px solid #999' }}>
                {formatString(item.fulfillmentStatus)}
              </td>
              <td align="start" className={`table-status px-3 whitespace-nowrap text-[16px] ${getStatusColor(item?.orderStatus)}`} style={{ borderBottom: '1px solid #999' }}>
                {formatString(item?.orderStatus) || 'N/A'}
              </td>
              <td align="center" className="px-3 py-4 whitespace-nowrap" style={{ borderBottom: '1px solid #999' }} onClick={e => e.stopPropagation()}>
                <ActionButton
                  icon={ThreeDots}
                  index={index}
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  actions={[
                    {
                      label: 'Update Status',
                      icon: FaEdit,
                      onClick: () => handleEditStatus(item),
                    },
                    {
                      label: item?.isRefundRequestSent
                        ? 'Refund Requested'
                        : processingRefundId === item.id
                          ? 'Processing...'
                          : 'Refund',
                      icon: FaUndo,
                      onClick: () => handleProcessRefund(item.id),
                      disabled:
                        item.isRefundRequestSent ||
                        processingRefundId === item.id,
                      className: `flex items-center text-[16px] font-montserrat bg-transparent border-none
                        ${item.isRefundRequestSent ||
                          processingRefundId === item.id
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-red-600 hover:text-red-800 cursor-pointer'
                        }`,
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {openModal && (
        <ModalComponent
          loading={updateStatusLoading}
          title={'Update Order Status'}
          children={
            <div className="w-[90%] mx-auto p-4">
              <FormInput
                name="status"
                type="select2"
                label="Order Status"
                height={52}
                placeholder="Select Order Status"
                options={updateStatusOptions}
                selectedValues={updateStatusOptions.find(
                  option => option.value === orderStatus,
                )}
                isOptionDisabled={(option) => {
                  const sequence = ['PENDING', 'CONFIRMED', 'IN_TRANSIT', 'DELIVERED', 'CANCELLED']
                  const currentIndex = sequence.indexOf(selectedOrderInitialStatus)
                  const optionIndex = sequence.indexOf(option.value)
                  if (currentIndex === -1 || optionIndex === -1) return false
                  return optionIndex < currentIndex
                }}
                validations={{
                  required: {
                    value: true,
                    message: 'Order Status is required',
                  },
                }}
                onChange={option => {
                  setOrderStatus(option.value)
                }}
                className="w-full"
              />
            </div>
          }
          onUpdate={handleUpdateStatus}
          setOpenModal={setOpenModal}
          setCancelModal={setCancelModal}
          cancelModal={cancelModal}
          openEditModal={openEditModal}
          setOpenEditModal={setOpenEditModal}
        />
      )}
    </div>
  )
}

export default OrderTable
